/**
 * CategorySelectionStep
 *
 * Step 3.5: User selects which product categories to onboard for AEO optimization.
 * This step ensures alignment between what's imported, processed, and measured.
 *
 * Part of AEO Onboarding Wizard
 * Date: February 18, 2026
 */

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, GitBranch, Layers, Loader2, Package } from 'lucide-react';

interface CategorySample {
  id: string;
  product_name: string;
  brand?: string;
  image_url?: string;
  price?: string;
  currency?: string;
}

interface Category {
  categoryId: string;
  displayName: string;
  productCount: number;
  samples: CategorySample[];
}

export type CategoryDiscoveryMode = 'ai_discovery' | 'feed_direct';

interface CategorySelectionStepProps {
  categories: Category[];
  totalProducts: number;
  onSubmit: (selectedCategories: string[], maxProducts: number, categoryDiscoveryMode: CategoryDiscoveryMode) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
}

export function CategorySelectionStep({
  categories,
  totalProducts,
  onSubmit,
  isLoading,
  error
}: CategorySelectionStepProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [maxProducts, setMaxProducts] = useState(100);
  const [discoveryMode, setDiscoveryMode] = useState<CategoryDiscoveryMode>('ai_discovery');

  const toggleCategory = (categoryId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(categoryId)) {
      newSelected.delete(categoryId);
    } else {
      newSelected.add(categoryId);
    }
    setSelected(newSelected);
  };

  const selectAll = () => setSelected(new Set(categories.map(c => c.categoryId)));
  const selectNone = () => setSelected(new Set());

  const selectedProductCount = categories
    .filter(c => selected.has(c.categoryId))
    .reduce((sum, c) => sum + c.productCount, 0);

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    await onSubmit(Array.from(selected), maxProducts, discoveryMode);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Select Categories to Onboard
        </CardTitle>
        <CardDescription>
          Choose which product categories to include in AEO optimization.
          {totalProducts > 0 && (
            <span className="block mt-1">
              Total: <Badge variant="outline">{totalProducts.toLocaleString()} products</Badge> across{' '}
              <Badge variant="outline">{categories.length} categories</Badge>
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Quick actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={selectAll} disabled={isLoading}>
            Select All
          </Button>
          <Button variant="outline" size="sm" onClick={selectNone} disabled={isLoading}>
            Clear
          </Button>
        </div>

        {/* Category grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <Card
              key={cat.categoryId}
              className={`cursor-pointer transition-all hover:border-primary ${
                selected.has(cat.categoryId) ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => !isLoading && toggleCategory(cat.categoryId)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <Checkbox
                    checked={selected.has(cat.categoryId)}
                    onCheckedChange={() => toggleCategory(cat.categoryId)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isLoading}
                  />
                  <Badge variant="secondary">{cat.productCount} products</Badge>
                </div>
                <CardTitle className="text-sm mt-2 line-clamp-2">
                  {cat.displayName || cat.categoryId}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {/* Sample product thumbnails */}
                <div className="flex gap-2 mt-2">
                  {cat.samples.slice(0, 3).map((sample) => (
                    <div key={sample.id} className="flex-1 min-w-0">
                      {sample.image_url ? (
                        <img
                          src={sample.image_url}
                          alt={sample.product_name}
                          className="w-full h-16 object-cover rounded"
                          onError={(e) => {
                            // Hide broken images
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-full h-16 bg-muted rounded flex items-center justify-center">
                          <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        {sample.product_name}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Category Discovery Mode */}
        <div className="space-y-3 pt-4 border-t">
          <Label>Category Discovery Mode</Label>
          <div className="grid md:grid-cols-2 gap-4">
            <Card
              className={`cursor-pointer transition-all hover:border-primary ${
                discoveryMode === 'ai_discovery' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => !isLoading && setDiscoveryMode('ai_discovery')}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-sm">AI-Powered Discovery</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                AI analyzes your products and creates optimized category groupings. Best for feeds with IDs or messy categories.
              </CardContent>
            </Card>
            <Card
              className={`cursor-pointer transition-all hover:border-primary ${
                discoveryMode === 'feed_direct' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : ''
              } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
              onClick={() => !isLoading && setDiscoveryMode('feed_direct')}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <GitBranch className="h-5 w-5 text-green-600" />
                  <CardTitle className="text-sm">Use Feed Categories</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                Use your existing feed taxonomy directly. All leaf categories get researched. Best for feeds with clean, descriptive category names.
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Max products setting */}
        <div className="space-y-2 pt-4 border-t">
          <Label htmlFor="maxProducts">Maximum products to process</Label>
          <Input
            id="maxProducts"
            type="number"
            value={maxProducts}
            onChange={(e) => setMaxProducts(parseInt(e.target.value) || 100)}
            min={10}
            max={1000}
            className="w-32"
            disabled={isLoading}
          />
          <p className="text-xs text-muted-foreground">
            Limits AI enrichment to control costs. Selected categories have{' '}
            {selectedProductCount.toLocaleString()} products.
          </p>
        </div>

        {/* Submit */}
        <Button
          onClick={handleSubmit}
          disabled={isLoading || selected.size === 0}
          size="lg"
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Processing...
            </>
          ) : (
            `Continue with ${selected.size} ${selected.size === 1 ? 'category' : 'categories'} (${Math.min(selectedProductCount, maxProducts).toLocaleString()} products)`
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
